import { useEffect, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Upload, Download, FileUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { ImportReviewModal } from "@/components/ImportReviewModal";

const EMPTY = { name: "", slug: "", tagline: "", image: "", order: 99 };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState(null);
  const { reload } = useCategories();
  const fileRef = useRef(null);
  const [importFor, setImportFor] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const exportCategory = async (c) => {
    try {
      const r = await api.get("/admin/products-export", { params: { category: c.slug }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `btm-products-${c.slug}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${c.name} products exported (${c.count})`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const importCategoryFile = () => {
    setReviewOpen(true);
  };

  const load = () => {
    setLoadError(false);
    api.get("/admin/categories")
      .then((r) => setCats(r.data.categories))
      .catch(() => setLoadError(true));
  };
  useEffect(() => {
    load();
  }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setEditing((e) => ({ ...e, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.existing) await api.put(`/admin/categories/${editing.slug}`, editing);
      else await api.post("/admin/categories", editing);
      toast.success(editing.existing ? "Category updated" : "Category created");
      setEditing(null);
      load();
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/categories/${c.slug}`);
      toast.success(`"${c.name}" deleted`);
      load();
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy">Categories</h1>
          <p className="text-sm text-slate-500 mt-1">
            Departments shown in the shop menu and home page. Changes go live immediately.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          className="h-10 px-5 rounded-full bg-navy text-white text-sm font-bold flex items-center gap-1.5"
          data-testid="add-category-btn"
        >
          <Plus size={15} /> Add category
        </button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="categories-grid">
        {loadError && (
          <div className="sm:col-span-2 xl:col-span-3 bg-red-50 border border-red-200 rounded-2xl p-6 text-center" data-testid="categories-load-error">
            <p className="text-sm font-bold text-red-600">Couldn't load categories — the server may have been restarting.</p>
            <button onClick={load} className="mt-3 h-9 px-5 rounded-full bg-navy text-white text-xs font-bold" data-testid="categories-retry-btn">
              Retry
            </button>
          </div>
        )}
        {!loadError && cats.length === 0 && (
          <div className="sm:col-span-2 xl:col-span-3 bg-cream border border-gold/30 rounded-2xl p-6 text-center text-sm text-slate-500" data-testid="categories-loading">
            Loading categories…
          </div>
        )}
        {cats.map((c) => (
          <div key={c.slug} className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden" data-testid={`category-row-${c.slug}`}>
            <div className="h-32 bg-slate-100">
              {c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display font-bold text-navy">{c.name}</p>
                  <p className="text-xs text-slate-400 font-mono">/{c.slug} · order {c.order}</p>
                  <p className="text-xs text-slate-500 mt-1">{c.tagline}</p>
                </div>
                <span className="text-[11px] font-bold bg-cream px-2 py-1 rounded-full text-navy shrink-0">
                  {c.count} products
                </span>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setEditing({ ...c, existing: true })}
                  className="flex-1 h-9 rounded-lg border border-slate-200 text-xs font-bold flex items-center justify-center gap-1.5 hover:border-gold transition-colors"
                  data-testid={`edit-category-${c.slug}`}
                >
                  <Pencil size={13} /> Edit
                </button>
                <button
                  onClick={() => exportCategory(c)}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold text-navy flex items-center justify-center gap-1.5 hover:border-gold transition-colors"
                  data-testid={`export-category-${c.slug}`}
                  title="Export this category's products as CSV"
                >
                  <Download size={13} /> Export
                </button>
                <button
                  onClick={() => { setImportFor(c); importCategoryFile(); }}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold text-navy flex items-center justify-center gap-1.5 hover:border-gold transition-colors"
                  data-testid={`import-category-${c.slug}`}
                  title="Upload a product list (CSV, Excel, PDF, Word) into this category — you'll review duplicates first"
                >
                  <FileUp size={13} /> Import
                </button>
                <button
                  onClick={() => remove(c)}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold text-red-500 flex items-center justify-center gap-1.5 hover:border-red-300 transition-colors"
                  data-testid={`delete-category-${c.slug}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {reviewOpen && importFor && (
        <ImportReviewModal
          category={importFor.slug}
          onClose={() => { setReviewOpen(false); setImportFor(null); }}
          onDone={() => { setReviewOpen(false); setImportFor(null); load(); reload(); }}
        />
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-navy-dark/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative bg-white rounded-3xl p-8 w-full max-w-lg my-10" data-testid="category-form">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-navy text-lg">
                {editing.existing ? `Edit ${editing.name}` : "New category"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="category-form-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Name</span>
                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="category-form-name" />
              </label>
              {!editing.existing && (
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Slug (blank = auto from name)</span>
                  <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="e.g. party-supplies" className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm font-mono outline-none focus:ring-2 focus:ring-gold" data-testid="category-form-slug" />
                </label>
              )}
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Tagline</span>
                <input value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="category-form-tagline" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Menu order</span>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 99 })} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="category-form-order" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Image URL</span>
                  <input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-xs font-mono outline-none focus:ring-2 focus:ring-gold" data-testid="category-form-image" />
                </label>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="h-10 px-4 rounded-lg bg-cream border border-slate-200 text-xs font-bold flex items-center gap-1.5 hover:border-gold transition-colors"
                  data-testid="category-form-upload"
                >
                  <Upload size={14} /> Upload image
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} data-testid="category-form-file" />
                {editing.image && <img src={editing.image} alt="preview" className="h-12 w-16 rounded-lg object-cover border border-slate-200" />}
              </div>
            </div>
            <button type="submit" className="mt-6 h-11 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors" data-testid="category-form-save">
              Save category
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
